// ABOUTME: Shared AI assistant chat surface rendered by both the floating
// desktop panel (variant "panel") and the full-screen /chat route (variant
// "fullscreen"). All behavior lives in useChatSurface; this file only varies
// the chrome (ChatTopBar) and content width. A team member's post waiting for the owner makes
// the next message its answer (useMemberReply).

import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ChatMessage } from '@/components/layout/ChatMessage'
import { ChatInput } from '@/components/layout/ChatInput'
import { ApprovalCard } from '@/components/approvals/ApprovalCard'
import { isPristineConversation } from '@/stores/aiChatWelcome'
import type { AIChatMessage } from '@/stores/aiChatTypes'
import { useAIChatStore } from '@/stores/useAIChatStore'
import { useUiStore } from '@/stores/useUiStore'
import { isApiError } from '@/api/client'
import { loadHermesChatHistory } from './hermesHistory'
import { WelcomeSuggestions } from './WelcomeSuggestions'
import { shouldRenderMessage } from './shouldRenderMessage'
import { ChatTopBar } from './ChatTopBar'
import { ThreadSourceFilter } from './ThreadSourceFilter'
import { usePlatformThread } from './usePlatformThread'
import { onComposeAboutRequest } from './platformRequestEvents'
import { useAskAbout, useComposeDraft } from '@/lib/askAbout'
import { useFirstAskStore } from '@/stores/useFirstAskStore'
import { buildTimeline } from './timeline'
import { RunActivityRow } from './RunActivityRow'
import { FileDropZone } from './FileDropZone'
import { useThreadRuns } from './useThreadRuns'
import { useChatApprovals, useChatCardActions } from './useChatApprovals'
import { useChatSurface } from './useChatSurface'
import { useMemberReply, type MemberPost } from './useMemberReply'
import { useReadAloud } from './useReadAloud'
import { LiveConversationOverlay } from './LiveConversationOverlay'
import { BrandKitConfirmCard } from './BrandKitConfirmCard'

// The canvas editor (fabric) loads only when an image is opened for annotation.
const AnnotationModal = lazy(() =>
  import('@/components/content/AnnotationModal').then((m) => ({ default: m.AnnotationModal }))
)

/** The newest message, when it is a team member's post on its brief. */
function latestMemberPost(messages: AIChatMessage[]): MemberPost | null {
  const last = messages.at(-1)
  const member = last?.metadata?.team_member
  if (!last || last.role !== 'assistant' || !member) return null
  return {
    messageId: last.metadata?.backend_message_id ?? last.id,
    briefId: member.brief_id,
    name: member.name,
  }
}

interface ChatSurfaceProps {
  /** panel = the 384px side panel beside a page; fullscreen = the /chat route */
  variant: 'panel' | 'fullscreen'
  /** Close handler for the panel variant's X button */
  onClose?: () => void
}

export function ChatSurface({ variant, onClose }: ChatSurfaceProps) {
  const {
    activeConversation,
    messages,
    creditsRemaining,
    isOffline,
    pendingCount,
    hasFailedMessage,
    retryLastMessage,
    handleRetryMessage,
    handleClearConversation,
    inputValue,
    setInputValue,
    handleSubmit,
    handleKeyDown,
    sendTurn,
    inputRef,
    scrollAreaRef,
    isRecording,
    voiceRecorderStatus,
    voiceRecorderError,
    isVoiceRecorderSupported,
    startVoiceRecording,
    stopVoiceRecording,
    voiceNote,
    sendVoiceNote,
    discardVoiceNote,
    pendingFiles,
    compressingCount,
    addFiles,
    removeFile,
    openFilePicker,
    fileInputRef,
    handleCopy,
    handleEditImage,
    handleDraftEdit,
    handleDraftSchedule,
    handleDraftDelete,
    handleScheduleResolve,
    handleScheduleDismiss,
    annotationImageUrl,
    setAnnotationImageUrl,
    handleEditComplete,
  } = useChatSurface()

  // A message with no body, no cards and no live typing indicator has nothing
  // to paint but its own timestamp. Filtering here rather than
  // returning null from ChatMessage also keeps the retry affordance's
  // "is this the last message" test honest.
  const platformThread = usePlatformThread(activeConversation?.id)
  const visibleMessages = useMemo(
    () => (platformThread.loading ? [] : messages.filter(shouldRenderMessage)),
    [messages, platformThread.loading]
  )
  // Approvals and each run's activity row sit in the conversation where they happened.
  const runs = useThreadRuns()
  const approvals = useChatApprovals('marketing')
  const cardActions = useChatCardActions()
  const timeline = useMemo(
    () => buildTimeline(visibleMessages, approvals, runs),
    [visibleMessages, approvals, runs]
  )
  const lastMessageId = visibleMessages.at(-1)?.id

  // A member's post waiting for the owner: the next message goes to that member, unless the
  // owner removes the chip or is asking Marketing about something.
  const showToast = useUiStore((state) => state.showToast)
  const { askAbout } = useAskAbout()
  const memberReply = useMemberReply(
    useMemo(() => latestMemberPost(visibleMessages), [visibleMessages])
  )
  const replyTarget = askAbout ? null : memberReply.target
  // The owner's words, typed or a voice note's transcript, go to the member as its answer.
  const sendToMember = async (text: string) => {
    if (!replyTarget || !activeConversation) return
    const store = useAIChatStore.getState()
    const team_member = { name: replyTarget.name, brief_id: replyTarget.briefId }
    const bubble = store.addMessage(activeConversation.id, {
      role: 'user',
      content: text,
      metadata: { team_member },
    })
    try {
      const ack = await memberReply.send(text, bubble)
      store.updateMessage(activeConversation.id, bubble, {
        status: 'sent',
        metadata: { team_member, backend_message_id: ack.message_id },
      })
    } catch (err) {
      store.removeMessage(activeConversation.id, bubble)
      setInputValue(text)
      showToast({
        type: 'error',
        title: isApiError(err) ? err.message : `Couldn't send to ${replyTarget.name}`,
      })
    }
  }
  const sendTypedToMember = () => {
    const text = inputValue.trim()
    if (!replyTarget || !text) return
    if (pendingFiles.length > 0) {
      showToast({
        type: 'error',
        title: `Files go to Marketing: remove the reply to ${replyTarget.name} to send them.`,
      })
      return
    }
    setInputValue('')
    void sendToMember(text)
  }
  const onSubmit = (e: React.FormEvent) => {
    if (!replyTarget) return void handleSubmit(e)
    e.preventDefault()
    sendTypedToMember()
  }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!replyTarget || e.key !== 'Enter' || e.shiftKey) return handleKeyDown(e)
    e.preventDefault()
    sendTypedToMember()
  }
  const onVoiceNoteSend = () => {
    if (!replyTarget || !voiceNote) return sendVoiceNote()
    const text = voiceNote.transcript.trim()
    if (!text) {
      showToast({
        type: 'error',
        title: `No words heard: record again or type your answer to ${replyTarget.name}.`,
      })
      return
    }
    discardVoiceNote()
    void sendToMember(text)
  }

  // Onboarding's first ask is sent once the thread has loaded, as the owner's first message.
  useEffect(() => {
    if (platformThread.loading || !activeConversation) return
    const ask = useFirstAskStore.getState().take()
    if (ask) sendTurn(ask.text, ask.attachments)
  }, [platformThread.loading, activeConversation, sendTurn])

  // Read aloud on each agent message, backed by POST /agent/speech. Kept out of useChatSurface
  // (already at the file-size ceiling) as its own concern.
  const { speakingId, loadingId, play, stop } = useReadAloud()

  // Live conversation mode — hands-free back-and-forth in its own full-screen overlay, opened
  // from the composer's voice button.
  const [showLiveConversation, setShowLiveConversation] = useState(false)
  const { draft, clear: clearDraft } = useComposeDraft()
  useEffect(() => {
    if (!draft) return
    setInputValue(draft)
    inputRef.current?.focus()
    clearDraft()
  }, [draft, clearDraft, setInputValue, inputRef])
  useEffect(
    () =>
      onComposeAboutRequest((text) => {
        setInputValue(text)
        inputRef.current?.focus()
      }),
    [setInputValue, inputRef]
  )
  // Live turns are posted to the same Hermes thread server-side, so closing
  // the call reloads it to show those transcripts and replies in the chat.
  const activeConversationId = activeConversation?.id
  const closeLiveConversation = useCallback(() => {
    setShowLiveConversation(false)
    if (!activeConversationId) return
    void loadHermesChatHistory()
      .then((restored) => {
        if (restored.length > 0) {
          useAIChatStore.getState().restoreMessages(activeConversationId, restored)
        }
      })
      .catch((err) => console.warn('Chat history reload failed:', err))
  }, [activeConversationId])

  const isFullscreen = variant === 'fullscreen'
  // Constrain message/input width on large fullscreen viewports; no-op at 384px.
  const contentWidth = isFullscreen ? 'mx-auto w-full max-w-[760px]' : undefined

  const conversationView = (
    <>
      <ChatTopBar
        title="Marketing"
        variant={variant}
        contentWidth={contentWidth}
        creditsRemaining={creditsRemaining}
        onClear={handleClearConversation}
        onClose={onClose}
      />

      <ThreadSourceFilter
        value={platformThread.source}
        sources={platformThread.sources}
        onChange={platformThread.setSource}
      />

      {/* Offline banner — the owner's only tell used to be "nothing
          happened": a message typed offline rendered exactly like a sent
          one, with no error and no indicator. `navigator.onLine` +
          window 'online'/'offline' (useChatSurface) drive this; it clears
          the instant connectivity returns. */}
      {isOffline && (
        <div
          className="bg-destructive/10 text-destructive border-y"
          data-testid="chat-offline-banner"
          role="status"
        >
          <div className={cn('flex items-center gap-2 px-3 py-1.5 text-xs', contentWidth)}>
            <span className="bg-destructive inline-flex size-2 shrink-0 rounded-full" />
            <span>You&apos;re offline — new messages won&apos;t send until you reconnect.</span>
          </div>
        </div>
      )}

      {/* Messages area.
          Radix renders the viewport's content in a `display: table` wrapper, which sizes to
          max-content — so a wide message (a deep-link card, a long unbroken caption) stretches
          it past the viewport and the column overflows horizontally. At 390px that clipped the
          deep-link arrows and the caption's right edge. Force that wrapper back to a normal
          full-width block. Scoped to this call site on purpose: LeadKanbanBoard uses a
          horizontal ScrollBar and genuinely needs the max-content behaviour. */}
      <ScrollArea
        className="min-h-0 flex-1 [&>[data-slot=scroll-area-viewport]>div]:!block [&>[data-slot=scroll-area-viewport]>div]:!w-full"
        ref={scrollAreaRef}
      >
        <div
          className={cn('space-y-2.5 px-4 py-5', contentWidth)}
          data-testid="chat-messages"
          data-tour="chat-thread"
        >
          {timeline.map((entry) =>
            entry.kind === 'approval' ? (
              <ApprovalCard
                key={entry.key}
                approval={entry.approval}
                {...cardActions(entry.approval.id)}
              />
            ) : entry.kind === 'run' ? (
              <RunActivityRow key={entry.key} run={entry.run} />
            ) : (
              <ChatMessage
                key={entry.key}
                message={entry.message}
                onCopy={handleCopy}
                onEditImage={handleEditImage}
                onDraftEdit={handleDraftEdit}
                onDraftSchedule={handleDraftSchedule}
                onDraftDelete={handleDraftDelete}
                onScheduleResolve={handleScheduleResolve}
                onScheduleDismiss={handleScheduleDismiss}
                onRetryMessage={handleRetryMessage}
                onRetry={
                  entry.message.isError && hasFailedMessage && entry.message.id === lastMessageId
                    ? retryLastMessage
                    : undefined
                }
                isSpeaking={speakingId === entry.message.id}
                isSpeechLoading={loadingId === entry.message.id}
                onPlaySpeech={play}
                onStopSpeech={stop}
              />
            )
          )}
          {/* Capability chips under the seeded welcome — pristine chats only.
              Living inside the scroll area means they can never push the
              composer below the fold, even at 390px. */}
          {isPristineConversation(activeConversation) && (
            <WelcomeSuggestions
              onPick={(prompt) => {
                setInputValue(prompt)
                inputRef.current?.focus()
              }}
            />
          )}
          <BrandKitConfirmCard />
        </div>
      </ScrollArea>

      {/* Input area — sticky bottom composer. The scheduling widget that used
          to sit pinned here renders inside the transcript now, from
          message.metadata.scheduling_card. */}
      <div className={cn('shrink-0', contentWidth)}>
        <ChatInput
          inputValue={inputValue}
          onInputChange={setInputValue}
          onSubmit={onSubmit}
          onKeyDown={onKeyDown}
          replyTo={replyTarget && { name: replyTarget.name, onRemove: memberReply.dismiss }}
          isRecording={isRecording}
          voiceRecorderStatus={voiceRecorderStatus}
          voiceRecorderError={voiceRecorderError}
          isVoiceRecorderSupported={isVoiceRecorderSupported}
          onVoiceStart={startVoiceRecording}
          onVoiceStop={stopVoiceRecording}
          voiceNote={voiceNote}
          onVoiceNoteSend={onVoiceNoteSend}
          onVoiceNoteDiscard={discardVoiceNote}
          onStartLive={() => setShowLiveConversation(true)}
          inputRef={inputRef}
          pendingCount={pendingCount}
          pendingFiles={pendingFiles}
          compressingCount={compressingCount}
          onFilesAdd={addFiles}
          onFileRemove={removeFile}
          onAttachClick={openFilePicker}
          fileInputRef={fileInputRef}
        />
      </div>
    </>
  )

  const annotationModal = annotationImageUrl && (
    <Suspense fallback={null}>
      <AnnotationModal
        open={!!annotationImageUrl}
        onOpenChange={(open) => {
          if (!open) setAnnotationImageUrl(null)
        }}
        imageUrl={annotationImageUrl}
        onEditComplete={handleEditComplete}
      />
    </Suspense>
  )

  if (variant === 'panel') {
    return (
      <>
        <FileDropZone onFiles={(files) => void addFiles(files)}>{conversationView}</FileDropZone>
        {annotationModal}
        <LiveConversationOverlay open={showLiveConversation} onClose={closeLiveConversation} />
      </>
    )
  }

  return (
    <div className="flex h-full min-h-0 w-full" data-testid="chat-surface-fullscreen">
      <FileDropZone onFiles={(files) => void addFiles(files)}>{conversationView}</FileDropZone>

      {annotationModal}
      <LiveConversationOverlay open={showLiveConversation} onClose={closeLiveConversation} />
    </div>
  )
}
